/**
 * errorEnvelope — FE ↔ SL error envelope contract and parser.
 *
 * Extracted from serviceLayerClient.ts 2026-04-24 for two reasons:
 *   1. guard-file-size LOC ceiling (300) on serviceLayerClient.ts.
 *   2. Contract clarity — this file IS the FE ↔ SL error contract surface.
 *      Having it live alone (schema + class + parser + docblock) makes
 *      drift impossible to hide in a thousand-line HTTP layer.
 *
 * See FE-SL-ERROR-ENVELOPE-V1 doc for the full contract narrative, and
 * the Rüdiger 2026-04-24 06:37:16 UTC incident comment below for the
 * regression the .passthrough() change prevents.
 */

import { z } from 'zod';

export const KNOWN_ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'INTERNAL',
  // per SL — explicit session-expiry envelope codes the gateway emits on a
  // dead/over-expiry session (distinct from a bare 401/UNAUTHORIZED). These
  // drive the global re-login overlay (see SESSION_EXPIRED_CODES below). They
  // are first-class members of the FE↔SL taxonomy so the overlay's trigger set
  // can be cross-checked against it (errorEnvelope.session.test.ts) — a future
  // divergence becomes a build/test failure, not a silent overlay regression
  // (BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31).
  'TOKEN_EXPIRED',
  'SESSION_EXPIRED',
] as const;
export type KnownServiceErrorCode = typeof KNOWN_ERROR_CODES[number];

/**
 * The subset of {@link KNOWN_ERROR_CODES} that unambiguously mean the SESSION
 * ITSELF is expired/invalid (as opposed to mere authorization denial), and so
 * should drive the global re-login overlay even when the gateway returned a
 * non-401 status.
 *
 * This is the SINGLE FE source of truth for the session-expiry trigger set.
 * It is declared as `KnownServiceErrorCode[]` so TypeScript rejects any member
 * that is not also a known taxonomy code at COMPILE time, and the runtime
 * conformance test (errorEnvelope.session.test.ts) re-asserts the same
 * invariant — binding the overlay's behaviour to the contract on both axes.
 *
 * Deliberately EXCLUDES `FORBIDDEN` and the bare `UNAUTHORIZED` code:
 *   - 403 FORBIDDEN = authenticated but not permitted on a VALID session. It
 *     must surface as a per-surface error, NOT blank the whole app behind the
 *     overlay. Including FORBIDDEN here was the 2026-06-01 all-dark regression
 *     (#1793): any 403 on any surface re-logged the entire operator out.
 *   - A real 401 is handled by the `status === 401` checks at the dispatch
 *     sites, so the `UNAUTHORIZED` envelope code adds nothing but the same
 *     false-blank risk on non-401 statuses.
 */
export const SESSION_EXPIRED_CODES: readonly KnownServiceErrorCode[] = [
  'TOKEN_EXPIRED',
  'SESSION_EXPIRED',
] as const;

/** Membership test for the session-expiry trigger set (string-tolerant: the
 *  wire `code` is `ServiceErrorCode = string`). */
export function isSessionExpiredCode(code: string): boolean {
  return (SESSION_EXPIRED_CODES as readonly string[]).includes(code);
}

// FE ↔ SL error envelope contract (see FE-SL-ERROR-ENVELOPE-V1 doc).
// SL sends `{error: {code, message, ...extras}}` flat at top level
// (exception_handlers.py:60-61 unwraps FastAPI's `detail` wrapper when
// `detail.error` is present). `extras` includes well-known recovery
// metadata like `existing_session_id` on 409 SESSION_ALREADY_ACTIVE;
// the field set is open-ended by design so SL can attach new recovery
// hints without forcing a coordinated FE schema bump.
//
// `.passthrough()` on the inner `error` object preserves every unknown
// key at runtime. The parser below lifts those keys into `err.details`
// so downstream consumers see them via the one well-known
// ServiceHttpError surface instead of string-parsing err.message.
// Rüdiger 2026-04-24 06:37:16 UTC incident ground truth: before
// passthrough, `existing_session_id` was silently stripped here,
// breaking 409 auto-adopt in useCallingSession and cascading into
// prequal_submit 422s when `session_id: null` then went over the wire.
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    // The service may send either `details` or legacy `context`.
    // `details` is an ARRAY on the SL's RequestValidationError handler
    // (service-layer-api/app/exception_handlers.py: `details: [{loc,msg,type}]`,
    // `request_id` None when no middleware set it). Before this union the schema
    // refused that exact body, so every SL 422 fell to the status tier and the
    // failing `loc` was reduced to "HTTP 422" (live 2026-09-29, visit-report v2).
    details: z.union([z.record(z.any()), z.array(z.any())]).optional(),
    context: z.record(z.any()).optional(),
    request_id: z.string().nullish(),
  }).passthrough(),
});

export type ServiceErrorCode = string;

export class ServiceHttpError extends Error {
  constructor(
    public readonly code: ServiceErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>,
    public readonly requestId?: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'ServiceHttpError';
  }
}

/** One FastAPI/pydantic field error as the SL emits it. */
export interface FieldValidationError {
  loc: Array<string | number>;
  msg: string;
  type?: string;
}

export function isFieldError(e: unknown): e is FieldValidationError {
  if (!e || typeof e !== 'object') return false;
  const o = e as { loc?: unknown; msg?: unknown };
  return Array.isArray(o.loc) && typeof o.msg === 'string';
}

/**
 * `details` as it may be LOGGED (PR #2204 review, thread 10). The error object keeps every validation issue WHOLE
 * (an issue can carry `input`, the rejected value, and `ctx`; unknown keys survive parsing on purpose); the log
 * line gets each issue as {loc, msg, type} only. Every other detail key is passed as it is. An entry that is not
 * {loc, msg}-shaped cannot be projected and is left out of the log line.
 */
export function detailsForLog(details: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (details === undefined || !Array.isArray(details.validation_errors)) return details;
  const issues = details.validation_errors.filter(isFieldError).map((i) => ({ loc: i.loc, msg: i.msg, type: i.type }));
  return { ...details, validation_errors: issues };
}

// A run of 7+ digits, optionally with the usual phone separators between them.
const PHONE_LIKE = /\+?\d(?:[\d\s/().-]{5,}\d)/g;

/**
 * Mask phone-like digit runs. An SL field `msg` can echo the rep's typed value (a
 * phone number in a free-text answer); anything derived from it that leaves memory —
 * err.message (sink, toast, console) or the telemetry projection — passes through
 * here. The ONE mask: submitFailureTelemetry imports it, never re-declares it.
 */
export function scrubPhoneLike(msg: string): string {
  return msg.replace(PHONE_LIKE, '[redacted-number]');
}

/** `loc` minus the leading `body`/`query` segment, joined — `next_when_date: …` (msg phone-masked). */
function formatFieldErrors(errors: FieldValidationError[]): string {
  return errors.map(e => `${e.loc.slice(1).join('.')}: ${scrubPhoneLike(e.msg)}`).join('; ');
}

export interface ParsedErrorResponse {
  code: ServiceErrorCode;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Parse an SL error response body into `{code, message, details}`.
 *
 * Three fallback tiers, in precedence order:
 *   1. Standard envelope `{error:{code,message,...extras}}` — zod
 *      passthrough preserves sibling extras, parser spreads them into
 *      `details` alongside the legacy `error.details`/`error.context`
 *      dict (legacy dict wins on key collision).
 *   2. FastAPI validation format `{detail:[{loc,msg,type},...]}` — build
 *      a human-readable message from the errors array.
 *   3. Non-envelope fallback — map status → code, set message to
 *      "HTTP <status>", surface raw body under `details.raw`.
 *
 * `body` is the parsed JSON body (or the raw string on non-JSON).
 * `isJson` signals whether body is structured (true) or text (false).
 */
export function parseErrorResponse(
  body: unknown,
  status: number,
  isJson: boolean,
): ParsedErrorResponse {
  const parsed = isJson ? errorEnvelopeSchema.safeParse(body) : null;
  const fastApiDetail =
    isJson && body && typeof body === 'object' && Array.isArray((body as { detail?: unknown }).detail)
      ? ((body as { detail: Array<{ loc: string[]; msg: string; type: string }> }).detail)
      : null;

  if (parsed && parsed.success) {
    // Preserve every unknown key on `error` alongside the legacy
    // details/context dict — see errorEnvelopeSchema comment above
    // for the Rüdiger 2026-04-24 incident that motivated this.
    // Merge order: spread `rest` (sibling extras) first, then overlay
    // details/context so the authoritative dict wins on key collision.
    const errorObj = parsed.data.error as Record<string, unknown>;
    const { code: _c, message: _m, details: d, context: ctx, request_id: _r, ...rest } = errorObj;
    void _c; void _m; void _r;
    const dictExtra =
      (d && typeof d === 'object' && !Array.isArray(d) ? d : null) ??
      (ctx && typeof ctx === 'object' && !Array.isArray(ctx) ? ctx : null) ??
      {};
    const merged: Record<string, unknown> = { ...rest, ...(dictExtra as Record<string, unknown>) };
    // SL validation envelope: lift the per-field list onto the SAME
    // `details.validation_errors` key the raw-FastAPI tier uses, and name the
    // failing fields in the message, so a 422 is diagnosable from err alone.
    const validationErrors = Array.isArray(d) ? d.filter(isFieldError) : [];
    if (validationErrors.length > 0) merged.validation_errors = validationErrors;
    return {
      code: parsed.data.error.code,
      message: validationErrors.length > 0
        ? `${parsed.data.error.message}: ${formatFieldErrors(validationErrors)}`
        : parsed.data.error.message,
      details: Object.keys(merged).length > 0 ? merged : undefined,
    };
  }

  if (fastApiDetail && fastApiDetail.length > 0) {
    return {
      code: 'VALIDATION_ERROR',
      message: formatFieldErrors(fastApiDetail),
      details: { validation_errors: fastApiDetail },
    };
  }

  const statusCode =
    status === 401 ? 'UNAUTHORIZED' :
    status === 403 ? 'FORBIDDEN' :
    status === 404 ? 'NOT_FOUND' :
    status === 409 ? 'CONFLICT' :
    status === 422 ? 'VALIDATION_ERROR' :
    status === 429 ? 'RATE_LIMITED' :
    'INTERNAL';

  return {
    code: statusCode,
    message: `HTTP ${status}`,
    details: isJson && body ? { raw: body } : undefined,
  };
}
